import React, {useEffect, useState} from 'react';

import omit from 'lodash/omit';
import pick from 'lodash/pick';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {CheckboxField} from 'app/general/widgets/CheckboxField';
import StateButton from 'app/general/widgets/StateButton';
import {
  createSignoffTemplate,
  fetchSignoffTemplate,
  updateSignoffTemplate,
} from 'app/signoffs/actions';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {
  SignoffTemplateSerializedType,
  TeamSerializedType,
} from 'sweet-types';

interface TemplateOptionsType {
  is_trigger_on_create: boolean;
  is_trigger_new_document: boolean;
  is_trigger_new_member: boolean;
  is_trigger_on_approval: boolean;
  is_trigger_on_signoff_requested: boolean;
  is_trigger_on_draft_approval: boolean;
}

interface Props {
  show: boolean;
  toggle: () => void;
  team: TeamSerializedType;
  refreshList: () => Promise<any>;
  className?: string;
}

export function ManageSignoffTemplateModal({
  show,
  toggle,
  team,
  refreshList,
}: Props): JSX.Element {
  const [templateOptions, setTemplateOptions] = useState<TemplateOptionsType>({
    is_trigger_on_create: true,
    is_trigger_new_document: true,
    is_trigger_new_member: true,
    is_trigger_on_approval: false,
    is_trigger_on_signoff_requested: true,
    is_trigger_on_draft_approval: true,
  });

  const updateOption = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = e.target;
    setTemplateOptions((options) => ({
      ...options,
      [name]: checked,
    }));
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const actions = useAsyncActions({
    createSignoffTemplate,
    updateSignoffTemplate,
  });

  const create = () =>
    (team.signofftemplate != null
      ? actions.updateSignoffTemplate({
          signofftemplate: team.signofftemplate,
          ...omit(templateOptions, ['is_trigger_on_create']),
        })
      : actions.createSignoffTemplate({
          team,
          ...templateOptions,
        })
    ).then(() => {
      toggle();
      refreshList();
    });

  useEffect(() => {
    // fetch signoff info when show is true.
    if (show && team.signofftemplate != null) {
      fetchSignoffTemplate(team.signofftemplate).then(
        (signofftemplate: SignoffTemplateSerializedType) => {
          setIsLoading(false);

          setTemplateOptions(
            pick(signofftemplate, [
              'is_trigger_new_document',
              'is_trigger_new_member',
              'is_trigger_on_approval',
              'is_trigger_on_signoff_requested',
              'is_trigger_on_create',
              'is_trigger_on_draft_approval',
            ])
          );
        },
        () => setIsLoading(false)
      );
      setIsLoading(true);
    }
  }, [show, team.signofftemplate]);

  return (
    <TabbedModal
      show={show}
      onCancel={toggle}
      tabs={[
        {
          eventKey: 'add-signoff',
          name:
            team.signofftemplate != null
              ? gt('Edit Sign Off Rules')
              : gt('Add Sign Off Rules'),
          icon: 'fa fa-signature',
          content: (
            <>
              <div className="form-group">
                <p>
                  {isLoading ? (
                    <span>
                      {gt('Loading...')}
                      <i className="fa fa-spinner fa-spin ml-1" />
                    </span>
                  ) : (
                    gt('Sign off required for:')
                  )}
                </p>
                {team.signofftemplate == null ? (
                  <CheckboxField
                    id="id_is_trigger_on_create"
                    name="is_trigger_on_create"
                    disabled={isLoading}
                    value={templateOptions['is_trigger_on_create']}
                    onChange={updateOption}
                    label={gt(
                      'Existing approved procedures, policies and processes.'
                    )}
                    labelClass="col-11"
                  />
                ) : null}
                <CheckboxField
                  id="id_is_trigger_new_document"
                  name="is_trigger_new_document"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_new_document']}
                  onChange={updateOption}
                  label={gt('New approved documents when added to this team.')}
                  labelClass="col-11"
                  helpText={gt(
                    'Sign off not sent for draft documents, only approved documents at the time they are added.'
                  )}
                />
                <CheckboxField
                  id="id_is_trigger_new_member"
                  name="is_trigger_new_member"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_new_member']}
                  onChange={updateOption}
                  label={gt('New members of this team.')}
                  labelClass="col-11"
                />
                <CheckboxField
                  id="id_is_trigger_on_approval"
                  name="is_trigger_on_approval"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_on_approval']}
                  onChange={updateOption}
                  label={gt('Each new document approval.')}
                  labelClass="col-11"
                />
                <CheckboxField
                  id="is_trigger_on_draft_approval"
                  name="is_trigger_on_draft_approval"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_on_draft_approval']}
                  onChange={updateOption}
                  label={gt('Draft documents that are approved.')}
                  labelClass="col-11"
                  helpText={gt(
                    'Sent when a draft document is approved for the first time.'
                  )}
                />
                <CheckboxField
                  id="is_trigger_on_signoff_requested"
                  name="is_trigger_on_signoff_requested"
                  disabled={isLoading}
                  value={templateOptions['is_trigger_on_signoff_requested']}
                  onChange={updateOption}
                  label={gt('Managers requesting sign off.')}
                  labelClass="col-11"
                  helpText={gt(
                    'Manually request sign off for individual documents.'
                  )}
                />
              </div>
              <StyledModalFooter className="mt-3">
                <button
                  onClick={toggle}
                  className="btn btn-link text-secondary"
                >
                  {gt('Close')}
                </button>
                <StateButton
                  onClick={create}
                  busyText={
                    team.signofftemplate != null
                      ? gt('Updating')
                      : gt('Creating')
                  }
                  className="btn-primary"
                >
                  {team.signofftemplate != null ? gt('Save') : gt('Create')}
                </StateButton>
              </StyledModalFooter>
            </>
          ),
        },
      ]}
    />
  );
}
